import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';

type RGBColor = {
  r: number;
  g: number;
  b: number;
};

type ColorGradient = {
  name: string;
  colors: string[];
};

// Predefined color schemes
const colorGradients: ColorGradient[] = [
  {
    name: 'green',
    colors: ['#40ffaa', '#184c06'],
  },
  {
    name: 'blue',
    colors: ['#0ea5e9', '#0c4a6e'],
  },
  {
    name: 'purple',
    colors: ['#a855f7', '#4c1d95'],
  },
  {
    name: 'orange',
    colors: ['#fb923c', '#7c2d12'],
  },
  {
    name: 'pink',
    colors: ['#ec4899', '#831843'],
  },
];

// Helper function to get color from name or hex
const getColorFromName = (colorName: string): string => {
  // If it's already a hex color, return it
  if (colorName?.startsWith('#')) {
    return colorName;
  }
  
  // Find the color scheme by name
  const scheme = colorGradients.find(cg => cg.name === colorName);
  if (scheme && scheme.colors.length > 0) {
    return scheme.colors[0]; // Return the first color from the scheme
  }
  
  // Default fallback
  return '#40ffaa';
};

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): RGBColor => {
  // Ensure we have a valid hex string
  const validHex = hex?.startsWith('#') ? hex : `#${hex}`;
  
  // Remove # if present
  const cleanHex = validHex.startsWith('#') ? validHex.slice(1) : validHex;
  
  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0;
  
  return { r, g, b };
};

// Helper to create rgba string
const rgba = (color: RGBColor, alpha: number): string => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
};

// Helper to interpolate between two colors
const interpolateColor = (color1: RGBColor, color2: RGBColor, factor: number): RGBColor => {
  return {
    r: Math.round(color1.r + factor * (color2.r - color1.r)),
    g: Math.round(color1.g + factor * (color2.g - color1.g)),
    b: Math.round(color1.b + factor * (color2.b - color1.b))
  };
};

type GlowBackgroundProps = {
  opacity?: number;
  colorScheme?: string | string[];
  position?: string;
  cycleColors?: boolean;
  cycleSpeed?: number; // in seconds
  width?: {
    mobile?: string;
    tablet?: string;
    desktop?: string;
  };
  height?: {
    mobile?: string;
    tablet?: string;
    desktop?: string;
  };
};

export const GlowBackground: React.FC<GlowBackgroundProps> = ({ 
  opacity = 0.13,
  colorScheme = 'blue',
  position = 'center-right',
  cycleColors = true,
  cycleSpeed = 5,
  width = {
    mobile: '100vw',
    tablet: '100vw',
    desktop: '100vw'
  },
  height = {
    mobile: '100vh',
    tablet: '100vh',
    desktop: '100vh'
  }
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  const [transitionFactor, setTransitionFactor] = useState(0);
  const [colors, setColors] = useState<string[]>([]);
  const animationRef = useRef<number>();

  // Determine which colors to use
  useEffect(() => {
    if (typeof colorScheme === 'string') {
      // Find the predefined color scheme
      const foundScheme = colorGradients.find(cg => cg.name === colorScheme);
      if (foundScheme) {
        setColors(foundScheme.colors);
      } else {
        // Default to green scheme if not found
        setColors(colorGradients[0].colors);
      }
    } else if (Array.isArray(colorScheme) && colorScheme.length > 0) {
      // Use provided array of colors
      setColors(colorScheme);
    } else {
      // Fallback to default
      setColors(colorGradients[0].colors);
    }
  }, [colorScheme]);

  // Handle color cycling animation
  useEffect(() => {
    if (!cycleColors || colors.length < 2) return;

    let startTime: number;
    const cycleDuration = cycleSpeed * 1000; // Convert to milliseconds
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      const progress = (elapsed % cycleDuration) / cycleDuration;
      setTransitionFactor(progress);
      
      // Check if we need to move to the next color
      if (elapsed >= cycleDuration) {
        startTime = timestamp;
        setCurrentColorIndex(prevIndex => (prevIndex + 1) % colors.length);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cycleColors, colors, cycleSpeed]);

  // Responsive width and height handlers
  const getResponsiveSize = () => {
    // Default values
    let currentWidth = width.desktop || '100vw';
    let currentHeight = height.desktop || '100vh';
    
    // Simple responsive detection based on window width
    if (typeof window !== 'undefined') {
      const windowWidth = window.innerWidth;
      
      if (windowWidth < 768) {
        // Mobile
        currentWidth = width.mobile || width.desktop || '100vw';
        currentHeight = height.mobile || height.desktop || '100vh';
      } else if (windowWidth < 1024) {
        // Tablet
        currentWidth = width.tablet || width.desktop || '100vw';
        currentHeight = height.tablet || height.desktop || '100vh';
      }
    }
    
    return { width: currentWidth, height: currentHeight };
  };

  // Get responsive dimensions
  const { width: responsiveWidth, height: responsiveHeight } = getResponsiveSize();

  // Set gradient position
  const gradientPosition = position.replace('-', ' ');
  
  // Calculate current colors for the gradient
  const getGradientColors = () => {
    if (!cycleColors || colors.length < 2) {
      // Get hex color from name or use directly if it's already a hex
      const primaryHex = getColorFromName(colors[0] || 'green');
      const secondaryHex = colors[1] ? getColorFromName(colors[1]) : primaryHex;
      
      return {
        primary: hexToRgb(primaryHex),
        secondary: hexToRgb(secondaryHex)
      };
    }
    
    // Get hex colors from color names or keep hex if already
    const currentHex = getColorFromName(colors[currentColorIndex]);
    const nextColorIndex = (currentColorIndex + 1) % colors.length;
    const nextHex = getColorFromName(colors[nextColorIndex]);
    
    // Convert to RGB
    const currentColor = hexToRgb(currentHex);
    const nextColor = hexToRgb(nextHex);
    
    return {
      primary: interpolateColor(currentColor, nextColor, transitionFactor),
      secondary: interpolateColor(currentColor, nextColor, transitionFactor * 0.8) // Slight delay for secondary
    };
  };

  const { primary, secondary } = getGradientColors();
  
  // Adjust fade-to color based on theme
  const fadeToColor = isDark ? 'rgba(0, 0, 0, 0)' : 'rgba(255, 255, 255, 0)';
  
  // Calculate derived opacity values
  const primaryOpacity = opacity;
  const secondaryOpacity = opacity * 0.6;
  const tertiaryOpacity = opacity * 0.4;
  const blurOpacity = opacity * 1.5;
  
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Main glow effect */}
      <div 
        className="absolute top-0 right-0"
        style={{
          width: responsiveWidth,
          height: responsiveHeight,
          background: `radial-gradient(circle at ${gradientPosition}, 
            ${isDark ? rgba(primary, primaryOpacity * 0.75) : rgba(primary, primaryOpacity)} 0%, 
            ${isDark ? rgba(secondary, secondaryOpacity * 0.75) : rgba(secondary, secondaryOpacity)} 30%, 
            ${fadeToColor} 60%)`,
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
        }}
      />
      
      {/* Secondary glow effect for added depth */}
      <div 
        className="absolute top-0 right-0 blur-xl"
        style={{
          width: responsiveWidth,
          height: responsiveHeight,
          opacity: blurOpacity,
          background: `radial-gradient(circle at ${gradientPosition}, 
            ${isDark ? rgba(primary, primaryOpacity) : rgba(primary, primaryOpacity * 1.25)} 0%, 
            ${isDark ? rgba(secondary, tertiaryOpacity) : rgba(secondary, tertiaryOpacity * 1.25)} 40%, 
            ${fadeToColor} 70%)`,
        }}
      />
    </div>
  );
};